import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import LayoutBarista from "../../../../layout/layout_barista";
import { AllMenuCategory, getCatagoryByID, Menu } from "../../../../api/category.api";
import { EditMenuRedux } from "../../../../reducer/menu_reducer";
import { ConfirmDelete } from "../../modal_page/confirmDelete";
import { patchMenuByID } from "../../../../api/menu.api";
import { ikThumb } from "../../../../utils/image";
import { isMenuAvailableNow, summarizeMenuAvailability } from "../../../../utils/schedule";

const EachCategory: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { categoryName, categoryId } = useParams();
  const categoryIdAsNumber = categoryId ? parseInt(categoryId) : 0;

  const [menus, setMenus] = useState<AllMenuCategory>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const fetchCategory = () => {
    setIsLoading(true);
    getCatagoryByID(categoryIdAsNumber).then((c) => {
      const filtered = c.menus.filter((m) => m.isAvailable === "available");
      setMenus(filtered);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdAsNumber]);

  const handleToggleActive = async (m: Menu) => {
    setTogglingId(m.id);
    try {
      await patchMenuByID(m.id, {
        isActive: m.isActive === "active" ? "inactive" : "active",
      });
      fetchCategory();
    } catch (err) {
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleEdit = (m: Menu) => {
    const payload: EditMenuRedux = {
      menu_id: m.id,
      name: m.name,
      description: m.description ?? "",
      category_id: categoryIdAsNumber,
      isActive: m.isActive,
      isAvailable: m.isAvailable,
      image: m.image ?? "",
      imageFileId: m.imageFileId ?? "",
    };
    dispatch({ type: "EDITMENU", payload });
    navigate(`/edit-menu/${m.id}`);
  };

  const handleDelete = (m: Menu) => {
    const payload: EditMenuRedux = {
      menu_id: m.id,
      name: m.name,
      description: m.description ?? "",
      category_id: categoryIdAsNumber,
      isActive: m.isActive,
      isAvailable: "unavailable",
      image: m.image ?? "",
      imageFileId: m.imageFileId ?? "",
    };
    dispatch({ type: "EDITMENU", payload });
    setShowDelete(true);
  };

  return (
    <LayoutBarista>
      <div className="min-h-screen bg-white font-sans pb-[120px]">
        <header className="px-5 pt-[24px] pb-[16px] border-b border-ink-100 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/menuManagement")}
            aria-label="Back"
            className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <p className="eyebrow-v2 text-ink-500">Category</p>
            <h1 className="font-display font-black text-[22px] leading-tight text-ink-1000 truncate">
              {categoryName}
            </h1>
          </div>
        </header>

        <div className="px-5 pt-[20px] pb-[16px]">
          <button
            type="button"
            onClick={() => navigate(`/add-menu/${categoryIdAsNumber}`)}
            className="btn-v2-primary w-full h-[48px] rounded-2xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New menu
          </button>
        </div>

        <div className="px-5 pb-[8px] flex items-end justify-between">
          <h2 className="font-display font-black text-[18px] tracking-tight text-ink-1000">Items</h2>
          <span className="text-xs text-ink-500 font-medium">{menus.length} items</span>
        </div>

        <main className="px-5">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="row-v2 animate-pulse">
                <div className="w-[64px] h-[64px] rounded-2xl bg-ink-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-[16px] bg-ink-200 rounded w-2/3" />
                  <div className="h-3 bg-ink-100 rounded w-1/2" />
                </div>
              </div>
            ))
          ) : menus.length === 0 ? (
            <div className="py-[64px] flex flex-col items-center justify-center text-center">
              <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <p className="font-display font-black text-lg text-ink-1000">No menus yet</p>
              <p className="text-sm text-ink-500 mt-1">Tap "New menu" to add the first one</p>
            </div>
          ) : (
            menus.map((m) => (
              <div key={m.id} className="row-v2">
                <div className="flex-shrink-0 w-[64px] h-[64px] rounded-2xl bg-ink-100 flex items-center justify-center overflow-hidden">
                  {m.image ? (
                    <img src={ikThumb(m.image, 200, 200)} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="font-display font-black text-2xl text-ink-300">
                      {m.name.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base text-ink-1000 truncate">{m.name}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${
                        m.isActive === "active" ? "bg-emerald-500" : "bg-ink-300"
                      }`}
                    />
                    <span className={`text-xs font-semibold ${
                      m.isActive === "active" ? "text-emerald-600" : "text-ink-500"
                    }`}>
                      {m.isActive}
                    </span>
                    {(m.availableDays || m.availableFromMin != null || m.availableToMin != null) && (
                      <span className={`inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        isMenuAvailableNow(m) ? "bg-emerald-50 text-emerald-700" : "bg-ink-100 text-ink-500"
                      }`}>
                        {summarizeMenuAvailability(m)}
                      </span>
                    )}
                    {m.description && m.description !== "-" && (
                      <span className="text-xs text-ink-500 truncate">· {m.description}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <label className="relative inline-flex items-center cursor-pointer mr-1" aria-label="Toggle active">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      disabled={togglingId === m.id}
                      checked={m.isActive === "active"}
                      onChange={() => handleToggleActive(m)}
                    />
                    <div className="w-9 h-5 bg-ink-200 rounded-full peer-checked:bg-accent transition-colors peer-disabled:opacity-50 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
                  </label>
                  <button
                    type="button"
                    onClick={() => handleEdit(m)}
                    aria-label="Edit menu"
                    className="w-[36px] h-[36px] rounded-full flex items-center justify-center text-ink-700 hover:bg-ink-100"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(m)}
                    aria-label="Delete menu"
                    className="w-[36px] h-[36px] rounded-full flex items-center justify-center text-red-600 hover:bg-red-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </main>

        <ConfirmDelete
          open={showDelete}
          onClose={() => setShowDelete(false)}
          onSuccess={fetchCategory}
        />
      </div>
    </LayoutBarista>
  );
};

export default EachCategory;
